import { i18n } from '@/i18n';
import { ORDER_MODE_TABLE_CONTENT_ID, TABLE_CONTENT_ID } from '../constants';
import { TableCommonViewModel } from '@/viewModels/tableContent';
import { ArrivalStatus } from '@/coreDomainsModule/entities/arrival';
import { StyleValue } from 'vue';
import { convertLeadTimeByUnit } from '@/coreDomainsModule/leadtime';
import { StdInventoryIndex } from '@/coreDomainsModule/entities/inventory';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { UserAccount } from '@/coreDomainsModule/entities/users';
import { UserAccountService } from '@/coreDomainsModule/services/userAccountService';

/**
 * 在庫管理に使用するテーブルのビューモデル
 */
export class InventoryTableViewModel {
  private t = i18n.t;
  private vm = new TableCommonViewModel();

  /**
   * テーブルのヘッダー名を取得
   * @param key - ヘッダーのキー
   */
  public getDisplayHeaderName = (key: string): string => {
    switch (key) {
      case 'warehouseNumber':
        return this.t('置場CD');
      case 'warehouse':
        return this.t('置き場名');
      case 'supplierNumber':
        return this.t('仕入先CD');
      case 'supplier':
        return this.t('仕入先');
      case 'businessNumber':
        return this.t('商品CD');
      case 'businessName':
        return this.t('商品名');
      case 'package':
        return this.t('容量');
      case 'unit':
        return this.t('単位');
      case 'customerNumber':
        return this.t('売上先CD');
      case 'customerName':
        return this.t('売上先名');
      case 'salesDestinationNumber':
        return this.t('送り先CD');
      case 'salesDestinationName':
        return this.t('送り先名');
      case 'forecastForReceiptName':
        return this.t('受領用内示名');
      case 'manufacturer':
        return this.t('製造社');
      case 'endUser':
        return this.t('エンドユーザー');
      case 'purchaseUnitPrice':
        return this.t('仕入単価');
      case 'salesUnitPrice':
        return this.t('販売単価');
      case 'leadTime':
        return this.t('リードタイム');
      case 'stdInventoryIndex':
        return this.t('標準在庫指数');
      case 'MOQ':
        return this.t('MOQ');
      case 'orderPackage':
        return this.t('発注単位(詳細ページ)');
      case 'supplierForecastResponsibleUser':
        return this.t('仕入担当者');
      case 'customerForecastResponsibleUser':
        return this.t('販売担当者');
      case 'createdAt':
        return this.t('作成日時');
      case 'updatedAt':
        return this.t('更新日時');
      case 'memo':
        return this.t('メモ');
      case 'alert':
        return this.t('アラート');
      default:
        return '';
    }
  };

  public transactionKeys = [
    'nowInventory',
    'arrival',
    'expectedArrival',
    'salesResult',
    'announce',
    'supplierAnnounce',
    'endInventory',
    'expectedEndInventory',
    'turnOver',
    'alert',
  ] as const;
  public transactionKeysForPreOrderMode = [
    'nowInventory',
    'alreadyArrival',
    'arrival',
    'expectedArrival',
    'amount',
    'desiredDeliveryDate',
    'createOrder',
    'salesResult',
    'announce',
    'supplierAnnounce',
    'endInventory',
    'expectedEndInventory',
    'turnOver',
    'alert',
    'previousMonthAccuracy', // 前月内示正答率
    'settingAccuracy', // 設定内示正答率
    'accuracyHistory', // 内示正答率履歴
  ] as const; // 発注モード時に当月以降のヘッダーに使用するキー

  public monthlyTransactionHeaderKeysForNormalMode: Record<string, (typeof this.transactionKeys)[number][]> = {
    past: ['supplierAnnounce', 'arrival', 'salesResult', 'announce', 'endInventory', 'alert'],
    now: ['nowInventory', 'supplierAnnounce', 'expectedArrival', 'salesResult', 'announce', 'expectedEndInventory', 'turnOver'],
    future: ['supplierAnnounce', 'expectedArrival', 'announce', 'expectedEndInventory', 'turnOver'],
  };

  public monthlyTransactionHeaderKeysForPreOrderMode: Record<string, (typeof this.transactionKeysForPreOrderMode)[number][]> = {
    past: ['supplierAnnounce', 'arrival', 'salesResult', 'announce', 'endInventory', 'alert'],
    now: [
      'nowInventory',
      'supplierAnnounce',
      'alreadyArrival',
      'expectedArrival',
      'previousMonthAccuracy',
      'settingAccuracy',
      'accuracyHistory',
      // 発注編集系
      'amount',
      'desiredDeliveryDate',
      'createOrder',
      'salesResult',
      'announce',
      'expectedEndInventory',
      'turnOver',
    ],
    future: [
      'supplierAnnounce',
      'alreadyArrival',
      'expectedArrival',
      'amount',
      'desiredDeliveryDate',
      'createOrder',
      'announce',
      'expectedEndInventory',
      'turnOver',
    ],
  };

  /**
   * 文字列がtransactionKeysであるかを判定する
   * @param value - 判定する文字列
   */
  public isTransactionKeys(value: string): value is (typeof this.transactionKeys)[number] {
    return (this.transactionKeys as readonly string[]).includes(value);
  }

  /**
   * 文字列がtransactionKeysForPreOrderModeであるかを判定する
   * @param value - 判定する文字列
   */
  public isTransactionKeysForPreOrderMode(value: string): value is (typeof this.transactionKeysForPreOrderMode)[number] {
    return (this.transactionKeysForPreOrderMode as readonly string[]).includes(value);
  }

  /**
   * 月とモードに対応するヘッダーキーを返す
   * @param month - 月
   * @param isOrderMode - 発注モードかどうか
   * @returns - テーブルのヘッダーのキー
   */
  public getMonthlyTransactionHeaderKeys(month: number, isOrderMode: boolean): readonly string[] {
    if (isOrderMode) {
      return this.monthlyTransactionHeaderKeysForPreOrderMode[month < 0 ? 'past' : month === 0 ? 'now' : 'future'];
    } else {
      return this.monthlyTransactionHeaderKeysForNormalMode[month < 0 ? 'past' : month === 0 ? 'now' : 'future'];
    }
  }

  // TODO: 削除予定
  /**
   * テーブルのヘッダーのキーを取得
   * @param key - ヘッダーのキー
   * @param month - 月
   */
  public getArrivalHeaderKey = (
    key: (typeof this.transactionKeys)[number] | (typeof this.transactionKeysForPreOrderMode)[number],
    month: number
  ): string => {
    return `transaction-${month}-${key}`;
  };

  /**
   * テーブルのヘッダー名を取得
   * @param key - ヘッダーのキー
   */
  public getTransactionHeaderName = (key: string): string => {
    switch (key) {
      case 'arrival':
      case 'expectedArrival':
        return this.t('入荷');
      case 'salesResult':
        return this.t('実績');
      case 'announce':
        return this.t('内示');
      case 'supplierAnnounce':
        return this.t('仕入先内示');
      case 'endInventory':
        return this.t('末在庫');
      case 'expectedEndInventory':
        return this.t('予末在庫');
      case 'nowInventory':
        return this.t('現在庫');
      case 'turnOver':
        return this.t('在庫回転率');
      case 'alert':
        return this.t('アラート');
      default:
        return '';
    }
  };

  /**
   * テーブルのヘッダー名を取得
   * @param key - ヘッダーのキー
   */
  public getTransactionHeaderNameForOrderMode = (key: string): string => {
    switch (key) {
      case 'alreadyArrival':
      case 'arrival':
        return this.t('入荷済');
      case 'expectedArrival':
        return this.t('予入荷');
      case 'amount':
        return this.t('発注数量');
      case 'desiredDeliveryDate':
        return this.t('希望納期');
      case 'createOrder':
        return this.t('発注作成');
      case 'salesResult':
        return this.t('実績');
      case 'announce':
        return this.t('内示');
      case 'supplierAnnounce':
        return this.t('仕入先内示');
      case 'endInventory':
        return this.t('末在庫');
      case 'expectedEndInventory':
        return this.t('予末在庫');
      case 'nowInventory':
        return this.t('現在庫');
      case 'turnOver':
        return this.t('在庫回転率');
      case 'alert':
        return this.t('アラート');
      case 'previousMonthAccuracy':
        return this.t('前月内示正答率').replace('\\n', '\n');
      case 'settingAccuracy':
        return this.t('設定内示正答率').replace('\\n', '\n');
      case 'accuracyHistory':
        return this.t('内示正答率履歴').replace('\\n', '\n');
      default:
        return '';
    }
  };

  /**
   * テーブルの列が数値列かどうかを判定する
   * @param key - キー
   */
  public isNumberColumn = (
    key:
      | (typeof this.vm.displayHeaders.inventoryControl)[number]
      | (typeof this.transactionKeys)[number]
      | (typeof this.transactionKeysForPreOrderMode)[number]
  ) => {
    return (
      key === 'package' ||
      key === 'MOQ' ||
      key === 'already-arrival' ||
      key === 'arrival' ||
      key === 'salesResult' ||
      key === 'announce' ||
      (key as string) === 'supplierAnnounce' ||
      key === 'endInventory'
    );
  };

  /**
   * テーブルのコンテンツIDを取得
   * @param isOrderMode - 発注モードかどうか
   */
  public getTableContentId = (isOrderMode: boolean): string => {
    return isOrderMode ? ORDER_MODE_TABLE_CONTENT_ID : TABLE_CONTENT_ID;
  };

  /**
   * バッジに付与するスタイル
   * @param status - ステータス
   * @param isOrderMode - 発注モードかどうか
   */
  public notificationBadgeStyle = (status: ArrivalStatus, isOrderMode: boolean): StyleValue => {
    switch (status) {
      case ArrivalStatus.Unpaid:
        return { gridRow: isOrderMode ? '' : '1 / 2' };
      case ArrivalStatus.ApprovalRequested:
        return { gridRow: isOrderMode ? '1 / 2' : '2 / 3' };
      case ArrivalStatus.Recommended:
        return { gridRow: isOrderMode ? '2 / 3' : '3 / 4' };
      default:
        return {};
    }
  };

  /**
   * 表示用に整形された標準在庫指数を返す関数
   * @param stdInventoryIndex - 標準在庫指数
   */
  public getFormattedStdInventoryIndex = (stdInventoryIndex?: { [key: string]: StdInventoryIndex }) => {
    if (!stdInventoryIndex || !stdInventoryIndex['標準在庫指数']) return '';
    const stdInventoryIndexObj = stdInventoryIndex['標準在庫指数'];
    return `${stdInventoryIndexObj.min ?? ''}~${stdInventoryIndexObj.max ?? ''}${stdInventoryIndexObj.option.unit ?? ''}`;
  };

  /**
   * 表示用に整形された標準在庫指数を返す関数
   * @param leadTime - リードタイム
   * @param unit - リードタイム単位
   */
  public getFormattedLeadTime = (leadTime?: number, unit?: string) => {
    return `${convertLeadTimeByUnit(leadTime ?? 0, unit ?? '')}${unit ?? ''}`;
  };

  /**
   * unixTimeを表示用の日付フォーマットに変換する関数
   * i18nも考慮する
   * @param unixTime - unixTime
   * @returns string (フォーマット済みの日付情報)
   */
  public getDisplayFormattedDateFromUnixTime = (unixTime: number | undefined): string => {
    if (!unixTime) return '--';
    // unixTimeをDateオブジェクトに変換
    const date = DateService.convertUnixTimeToDate(unixTime);

    // 各時間成分を取り出してゼロ埋めして文字列を作る
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    // 時間情報を作成する
    const timeOnly = `${hours}:${minutes}:${seconds}`;

    // 日付情報を作成する
    const i18nDateString = this.t('displayDate', {
      year: date.getFullYear(),
      month: DateService.convertMonthWithZero(date.getMonth() + 1),
      day: date.getDate(),
    });
    return `${i18nDateString} ${timeOnly}`;
  };

  /**
   * 表示用に成形されたユーザー名を返す
   * @param users - 表示するユーザー一覧
   */
  public getUserNames = (users?: UserAccount[]) => {
    if (!users || users.length === 0) return '--';
    return users.map((user) => UserAccountService.getDisplayName(user)).join(', ');
  };

  /**
   * ヘッダー名からデータを取得する
   * @param item - アイテム
   * @param headerKey - ヘッダーキー
   * @param isChild - 子取引かどうか
   * @returns プロパティ名
   */
  public getPropertyFromKey = (item: any, headerKey: string, isChild = false) => {
    if (isChild) {
      // 子要素
      switch (headerKey) {
        case 'customerForecastResponsibleUser':
          return item.baseBusinessResponsibleUsers?.[0]?.name || '--';
        case 'forecastForReceiptName':
          return item.forecastForReceiptName ?? '--';
        default:
          return item[headerKey] ?? '';
      }
    }
    switch (headerKey) {
      // 親要素
      case 'supplier':
        return item['supplierName'];
      case 'leadTime':
        return this.getFormattedLeadTime(item.leadTime, item.leadTimeUnit) ?? '--';
      case 'stdInventoryIndex':
        return this.getFormattedStdInventoryIndex(item.stdInventoryIndices) ?? '--';
      case 'orderPackage':
        return item.orderPackage ? item.orderPackage : '--';
      case 'supplierForecastResponsibleUser':
        return this.getUserNames(item.aggregateBusinessResponsibleUsers) || '--';
      case 'customerForecastResponsibleUser':
        return '--';
      case 'forecastForReceiptName':
        return '--';
      case 'updatedAt':
      case 'createdAt':
        return item[headerKey] ? DateService.getDisplayYearMonthDay(new Date(item[headerKey] * 1000)) : '--';
      default:
        return item[headerKey];
    }
  };

  /**
   * ヘッダーキーが数値ヘッダーかどうかを判定する
   * @param headerKey - ヘッダーキー
   * @returns 数値ヘッダーかどうか
   */
  public isNumberHeader = (headerKey: string): boolean => {
    return (
      headerKey === 'package' ||
      headerKey === 'MOQ' ||
      headerKey === 'orderPackage' ||
      headerKey === 'purchaseUnitPrice' ||
      headerKey === 'salesUnitPrice'
    );
  };

  public DEFAULT_HIDDEN_HEADER_KEYS = [
    'unit',
    'manufacturer',
    'endUser',
    'purchaseUnitPrice',
    'salesUnitPrice',
    'leadTime',
    'stdInventoryIndex',
    'MOQ',
    'supplierForecastResponsibleUser',
    'customerForecastResponsibleUser',
    'updatedAt',
    'createdAt',
    'memo',
  ];
}
